import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import client from '../api/client';
import TasksHeader from '../components/TasksHeader';
import TaskCard from '../components/TaskCard';
import TaskModal from '../components/TaskModal';
import { Search, Filter, User as UserIcon, Calendar, Plus, RefreshCcw, ChevronRight } from 'lucide-react';
import { useDevice } from '../hooks/useDevice';
import {
    DndContext,
    closestCenter,
    PointerSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import {
    SortableContext,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';

const Tasks = () => {
    const queryClient = useQueryClient();
    const { isMobile } = useDevice();
    const [filters, setFilters] = useState({
        assignee_id: '',
        deadline: 'all',
        search: ''
    });
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [taskToEdit, setTaskToEdit] = useState(null);

    // Fetch settings
    const { data: cfg } = useQuery({
        queryKey: ['settings'],
        queryFn: async () => (await client.get('/settings')).data,
        refetchOnWindowFocus: false
    });

    // Fetch all users for filter
    const { data: allUsers = [] } = useQuery({
        queryKey: ['users'],
        queryFn: async () => (await client.get('/users')).data
    });

    // Fetch internal tasks
    const { data: tasks = [], isLoading } = useQuery({
        queryKey: ['internal-tasks', filters],
        queryFn: async () => {
            const res = await client.get('/tasks', { 
                params: {
                    search: filters.search,
                    assignee_id: filters.assignee_id
                }
            });
            return res.data;
        },
        refetchOnWindowFocus: false
    });

    const updateTaskMutation = useMutation({
        mutationFn: ({ id, data }) => client.patch(`/tasks/${id}`, data),
        onSuccess: () => {
            queryClient.invalidateQueries(['internal-tasks']);
        }
    });

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 8,
            },
        })
    );

    const handleDragEnd = (event) => {
        const { active, over } = event;
        if (!over) return;
        const taskId = active.id;
        const newStatus = over.id;
        if (active.data.current.status !== newStatus) {
            updateTaskMutation.mutate({
                id: taskId,
                data: { status: newStatus }
            });
        }
    };

    const tasksByColumn = useMemo(() => {
        const statuses = [
            { id: 'Новая', title: 'Новая' },
            { id: 'В работе', title: 'В работе' },
            { id: 'На согласовании', title: 'На согласовании' },
            { id: 'Готово', title: 'Готово' }
        ];
        const grouped = {};
        statuses.forEach(s => { grouped[s.id] = { column: s, tasks: [] }; });
        tasks.forEach(task => {
            let status = task.status || 'Новая';
            if (status === 'На проверке' || status === 'Review') status = 'На согласовании';
            if (status === 'Выполнена' || status === 'Завершена' || status === 'DONE') status = 'Готово';
            if (grouped[status]) grouped[status].tasks.push(task);
        });
        return grouped;
    }, [tasks]);

    const handleNewTask = () => {
        setTaskToEdit(null);
        setIsModalOpen(true);
    };

    const handleTaskClick = (task) => {
        setTaskToEdit(task);
        setIsModalOpen(true);
    };

    // --- RESPONSIVE UI ---
    return (
        <div className="flex flex-col h-screen bg-slate-950 italic">
            {/* Standardized Header */}
            <header className="flex flex-col md:flex-row md:items-center justify-between p-4 md:px-8 md:py-6 gap-6 md:gap-4 border-b border-neutral-800 bg-neutral-900/20">
                <div>
                     <h1 className="text-xl md:text-3xl font-black italic tracking-tighter uppercase leading-none">Бэклог Задач</h1>
                     <p className="text-[10px] text-neutral-500 font-black uppercase tracking-widest mt-1 italic">Venture Task Management</p>
                </div>
                <div className="flex items-center gap-3 w-full md:w-auto">
                    <div className="relative flex-1 md:w-80">
                        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-600" />
                        <input 
                            type="text" 
                            placeholder="Поиск..." 
                            value={filters.search} 
                            onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))} 
                            className="w-full bg-neutral-950 border border-neutral-800 rounded-2xl py-3 md:py-4 pl-12 text-sm text-white font-bold outline-none focus:border-blue-500" 
                        />
                    </div>
                    <button onClick={handleNewTask} className="w-12 h-12 md:h-14 md:w-14 bg-white text-black rounded-2xl flex items-center justify-center shadow-xl active:scale-95 transition-all shrink-0">
                        <Plus size={24}/>
                    </button>
                </div>
            </header>

            {/* Desktop-only Filters Bar */}
            <div className="hidden md:block bg-neutral-900/40 border-b border-neutral-800 p-4">
                <div className="flex items-center gap-6 max-w-7xl mx-auto">
                    <div className="flex items-center gap-2">
                        <UserIcon size={14} className="text-blue-500" />
                        <select 
                            value={filters.assignee_id} 
                            onChange={(e) => setFilters(prev => ({ ...prev, assignee_id: e.target.value }))} 
                            className="bg-neutral-950 border border-neutral-800 rounded-xl px-4 py-2 text-[10px] font-black uppercase text-white outline-none focus:border-blue-500"
                        >
                            <option value="">Все исполнители</option>
                            {allUsers.map(u => <option key={u.id} value={u.id}>{u.fio || u.name}</option>)}
                        </select>
                    </div>
                    <button onClick={() => queryClient.invalidateQueries(['internal-tasks'])} className="p-2 text-neutral-500 hover:text-white transition-all"><RefreshCcw size={18}/></button>
                </div>
            </div>

            {/* Main Content Area */}
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                <div className="flex-1 overflow-x-auto overflow-y-auto md:p-8 p-4 bg-zinc-950/50">
                    {isLoading ? (
                        <div className="h-full flex items-center justify-center opacity-20">
                            <RefreshCcw className="animate-spin text-white mb-4" size={48}/>
                        </div>
                    ) : (
                        <div className="flex flex-col md:flex-row gap-6 md:h-full items-start">
                            {Object.entries(tasksByColumn).map(([columnId, { column, tasks: columnTasks }]) => (
                                <SortableContext 
                                    key={columnId} 
                                    id={columnId} 
                                    items={columnTasks.map(t => t.id)} 
                                    strategy={verticalListSortingStrategy}
                                >
                                    <div className="w-full md:w-80 bg-neutral-900/20 border border-neutral-800/50 rounded-[2.5rem] p-5 flex flex-col md:max-h-full">
                                        <div className="flex items-center justify-between mb-6 px-2">
                                            <div className="flex items-center gap-2">
                                                <div className="w-1.5 h-1.5 rounded-full bg-blue-500 shadow-[0_0_8px_rgba(59,130,246,0.5)]" />
                                                <h3 className="text-white font-black text-sm md:text-sm uppercase italic tracking-wider leading-none">{column.title}</h3>
                                            </div>
                                            <span className="text-[10px] font-black text-neutral-500 bg-neutral-950 px-3 py-1 rounded-full border border-neutral-800">{columnTasks.length}</span>
                                        </div>
                                        
                                        <div className="flex-1 space-y-4 custom-scrollbar pr-1">
                                            {columnTasks.length === 0 ? (
                                                <div className="text-center py-12 md:py-20 opacity-5 border border-dashed border-white/20 rounded-3xl">
                                                    <Plus size={isMobile ? 24 : 48} className="mx-auto"/>
                                                    <p className="text-[9px] font-black uppercase mt-4">Пусто</p>
                                                </div>
                                            ) : (
                                                columnTasks.map(task => (
                                                    <TaskCard key={task.id} task={{ ...task, columnName: column.title }} onTaskClick={handleTaskClick} />
                                                ))
                                            )}
                                        </div>
                                    </div>
                                </SortableContext>
                            ))}
                        </div>
                    )}
                </div>
            </DndContext>

            {/* Mobile Task Modal Footer Spacer */}
            <div className="md:hidden h-20" />

            {/* Shared Modal */}
            <TaskModal 
                isOpen={isModalOpen} 
                taskToEdit={taskToEdit} 
                onClose={() => { setIsModalOpen(false); setTaskToEdit(null); }} 
                onSuccess={() => queryClient.invalidateQueries(['internal-tasks'])} 
            />
        </div>
    );
};

export default Tasks;
